import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertCircle, ClipboardList, ListChecks, XCircle, Video, ChevronDown, ChevronUp, Clock, Server } from 'lucide-react';
import { getAllDiscountDaysLogs, getFailedDiscountDaysLogs } from '../api/discountDaysLogs.api';
import { formatDateTime24 } from '../../utils/formatTime24';
import { getVideoBatchRuns, getVideoBatchRunDetail } from '../api/videoBatch.api';
import { getAllFtpUsers } from '../api/ftpUser.api';

const FTP_STATUS_COLUMNS = [
  { key: 'username', label: 'feedId' },
  { key: 'customerId', label: 'customerId' },
  { key: 'createdAt', label: 'createdAt' },
  { key: 'pathDefault', label: 'Path' },
  { key: 'camIdentifier', label: 'nombre' },
];

const DATE_KEYS = ['createdAt', 'date', 'timestamp', 'executedAt', 'processedAt', 'logDate', 'updatedAt'];

const findDateKey = (item) => {
  if (!item) return null;
  return DATE_KEYS.find((key) => item[key] !== undefined) ||
    Object.keys(item).find((key) => /date|time|_at$/i.test(key));
};

const sortByMostRecent = (items) => {
  if (!Array.isArray(items) || items.length === 0) return items;
  const dateKey = findDateKey(items[0]);
  if (!dateKey) return items;
  return [...items].sort((a, b) => new Date(b[dateKey]).getTime() - new Date(a[dateKey]).getTime());
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === '') return '---';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'string' && !Number.isNaN(Date.parse(value)) && /\d{4}-\d{2}-\d{2}/.test(value)) {
    return formatDateTime24(value);
  }
  return String(value);
};

const DiscountDaysLogsTab = () => {
  const [activeView, setActiveView] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLogs = async (view) => {
    setActiveView(view);
    setLoading(true);
    setError(null);
    try {
      const data = view === 'all' ? await getAllDiscountDaysLogs() : await getFailedDiscountDaysLogs();
      const items = Array.isArray(data) ? data : (data?.items || data?.logs || []);
      setLogs(sortByMostRecent(items));
    } catch (err) {
      setError('Error al obtener los logs de descuento de días');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const columns = logs.length > 0 ? Object.keys(logs[0]) : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4">
        <button
          onClick={() => fetchLogs('all')}
          disabled={loading}
          className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-sm transition-all shadow-sm disabled:opacity-50 ${
            activeView === 'all' ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <ListChecks size={18} />
          Todos los Logs
        </button>
        <button
          onClick={() => fetchLogs('failed')}
          disabled={loading}
          className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-sm transition-all shadow-sm disabled:opacity-50 ${
            activeView === 'failed' ? 'bg-red-600 text-white shadow-lg shadow-red-200' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <XCircle size={18} />
          Logs de Fallos
        </button>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <RefreshCw className="animate-spin text-blue-500 w-10 h-10" />
          <p className="text-gray-500 font-medium">Obteniendo logs...</p>
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border border-red-100 p-8 rounded-[2.5rem] text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={40} />
          <p className="text-red-800 font-bold">{error}</p>
        </div>
      )}

      {!loading && !error && activeView && logs.length === 0 && (
        <div className="text-center py-20 bg-slate-100 rounded-[2.5rem] border-2 border-dashed border-slate-200">
          <ClipboardList className="text-slate-300 mx-auto mb-4" size={48} />
          <p className="text-slate-400 font-bold italic">No se encontraron logs</p>
        </div>
      )}

      {!loading && !error && logs.length > 0 && (
        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
          <h3 className="text-lg font-bold text-slate-800 mb-6">
            {activeView === 'all' ? 'Todos los Logs' : 'Logs de Fallos'}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  {columns.map((col) => (
                    <th key={col} className="text-left py-3 px-4 font-bold text-slate-700 whitespace-nowrap">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((item, idx) => (
                  <tr key={item.id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                    {columns.map((col) => (
                      <td key={col} className="py-3 px-4 text-slate-700 whitespace-nowrap">{formatCellValue(item[col])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-4">Total de {logs.length} registros</p>
        </div>
      )}
    </div>
  );
};

const STATUS_STYLES = {
  COMPLETED: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  PENDING: 'bg-amber-100 text-amber-700',
  PROCESSING: 'bg-blue-100 text-blue-700',
};

const formatDateTime = (value) => {
  if (!value) return '---';
  return formatDateTime24(value, { seconds: true });
};

const VideoBatchLogsTab = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [detailByRunId, setDetailByRunId] = useState({});
  const [detailLoading, setDetailLoading] = useState(null);
  const [detailError, setDetailError] = useState(null);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getVideoBatchRuns();
      setRuns(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Error al obtener las corridas de video-batch');
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  const toggleRun = async (runId) => {
    if (expandedRunId === runId) {
      setExpandedRunId(null);
      return;
    }
    setExpandedRunId(runId);
    if (detailByRunId[runId]) return;

    setDetailLoading(runId);
    setDetailError(null);
    try {
      const detail = await getVideoBatchRunDetail(runId);
      setDetailByRunId((prev) => ({ ...prev, [runId]: detail }));
    } catch (err) {
      setDetailError('Error al obtener el detalle de la corrida');
    } finally {
      setDetailLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-slate-500 font-medium text-sm">Corridas del cronjob de generación de videos (últimos 90 días)</p>
        <button
          onClick={fetchRuns}
          disabled={loading}
          className="flex items-center gap-2 min-h-[44px] lg:min-h-0 px-5 py-2.5 rounded-2xl font-bold text-sm bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 disabled:opacity-50 transition-all shadow-sm"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <RefreshCw className="animate-spin text-blue-500 w-10 h-10" />
          <p className="text-gray-500 font-medium">Obteniendo corridas...</p>
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border border-red-100 p-8 rounded-[2.5rem] text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={40} />
          <p className="text-red-800 font-bold">{error}</p>
        </div>
      )}

      {!loading && !error && runs.length === 0 && (
        <div className="text-center py-20 bg-slate-100 rounded-[2.5rem] border-2 border-dashed border-slate-200">
          <Video className="text-slate-300 mx-auto mb-4" size={48} />
          <p className="text-slate-400 font-bold italic">No se encontraron corridas</p>
        </div>
      )}

      {!loading && !error && runs.length > 0 && (
        <div className="space-y-4">
          {runs.map((run) => {
            const isExpanded = expandedRunId === run.runId;
            const detail = detailByRunId[run.runId];
            const isDetailLoading = detailLoading === run.runId;

            return (
              <div key={run.runId} className="bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden">
                <button
                  onClick={() => toggleRun(run.runId)}
                  className="w-full flex items-center justify-between gap-4 p-6 text-left hover:bg-slate-50 transition-all"
                >
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2 text-slate-800 font-bold">
                      <Clock size={16} className="text-slate-400" />
                      {formatDateTime(run.startedAt)}
                    </div>
                    <span className="text-slate-300">→</span>
                    <span className="text-slate-500 text-sm font-medium">{formatDateTime(run.lastActivityAt)}</span>
                    <span className="text-xs font-bold text-slate-400 bg-slate-100 px-3 py-1 rounded-full">
                      {run.totalDispatched} despachados
                    </span>
                    {run.pendingCount > 0 && (
                      <span className="text-xs font-bold text-amber-700 bg-amber-100 px-3 py-1 rounded-full">
                        {run.pendingCount} pendientes
                      </span>
                    )}
                    {Object.entries(run.statusCounts || {}).map(([status, count]) => (
                      <span
                        key={status}
                        className={`text-xs font-bold px-3 py-1 rounded-full ${STATUS_STYLES[status] || 'bg-slate-100 text-slate-600'}`}
                      >
                        {status}: {count}
                      </span>
                    ))}
                  </div>
                  {isExpanded ? <ChevronUp size={20} className="text-slate-400 shrink-0" /> : <ChevronDown size={20} className="text-slate-400 shrink-0" />}
                </button>

                {isExpanded && (
                  <div className="border-t border-slate-100 p-6 bg-slate-50">
                    {isDetailLoading && (
                      <div className="flex items-center gap-3 py-6 justify-center">
                        <RefreshCw className="animate-spin text-blue-500 w-6 h-6" />
                        <p className="text-gray-500 font-medium">Obteniendo detalle...</p>
                      </div>
                    )}
                    {!isDetailLoading && detailError && (
                      <p className="text-red-800 font-bold text-center py-4">{detailError}</p>
                    )}
                    {!isDetailLoading && !detailError && detail && (
                      detail.failures && detail.failures.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-slate-200">
                                <th className="text-left py-2 px-4 font-bold text-slate-700">Cámara</th>
                                <th className="text-left py-2 px-4 font-bold text-slate-700">Motivo de fallo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail.failures.map((failure, idx) => (
                                <tr key={`${failure.cameraId}-${idx}`} className="border-b border-slate-100">
                                  <td className="py-2 px-4 text-slate-700 whitespace-nowrap">{failure.cameraId}</td>
                                  <td className="py-2 px-4 text-slate-700">{failure.failureReason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-slate-400 font-medium italic text-center py-4">Sin fallos en esta corrida</p>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FtpStatusLogsTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllFtpUsers();
      setUsers(sortByMostRecent(Array.isArray(data) ? data : []));
    } catch (err) {
      setError('Error al obtener el estado del FTP');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-slate-500 font-medium text-sm">Registros de usuarios FTP</p>
        <button
          onClick={fetchUsers}
          disabled={loading}
          className="flex items-center gap-2 min-h-[44px] lg:min-h-0 px-5 py-2.5 rounded-2xl font-bold text-sm bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 disabled:opacity-50 transition-all shadow-sm"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <RefreshCw className="animate-spin text-blue-500 w-10 h-10" />
          <p className="text-gray-500 font-medium">Obteniendo estado del FTP...</p>
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border border-red-100 p-8 rounded-[2.5rem] text-center">
          <AlertCircle className="text-red-500 mx-auto mb-4" size={40} />
          <p className="text-red-800 font-bold">{error}</p>
        </div>
      )}

      {!loading && !error && users.length === 0 && (
        <div className="text-center py-20 bg-slate-100 rounded-[2.5rem] border-2 border-dashed border-slate-200">
          <Server className="text-slate-300 mx-auto mb-4" size={48} />
          <p className="text-slate-400 font-bold italic">No se encontraron registros de FTP</p>
        </div>
      )}

      {!loading && !error && users.length > 0 && (
        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  {FTP_STATUS_COLUMNS.map((col) => (
                    <th key={col.key} className="text-left py-3 px-4 font-bold text-slate-700 whitespace-nowrap">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((user, idx) => (
                  <tr key={user.id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                    {FTP_STATUS_COLUMNS.map((col) => (
                      <td key={col.key} className="py-3 px-4 text-slate-700 whitespace-nowrap">{formatCellValue(user[col.key])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-4">Total de {users.length} registros</p>
        </div>
      )}
    </div>
  );
};

const TABS = [
  { id: 'discountDays', label: 'Descuento de días', component: DiscountDaysLogsTab },
  { id: 'videoBatch', label: 'Videos Generados', component: VideoBatchLogsTab },
  { id: 'ftpStatus', label: 'Estado FTP', component: FtpStatusLogsTab },
];

const LogsDashboard = () => {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const ActiveComponent = TABS.find((tab) => tab.id === activeTab)?.component;

  return (
    <div className="p-8 bg-slate-50 min-h-screen font-sans">
      <div className="mb-10">
        <h1 className="text-3xl font-black text-slate-800 tracking-tight">Logs</h1>
        <p className="text-slate-500 font-medium mt-1">Historial de procesos del sistema</p>
      </div>

      <div className="flex bg-white p-2 rounded-3xl border border-slate-100 w-fit gap-2 mb-8 shadow-sm overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-sm transition-all ${
              activeTab === tab.id ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {ActiveComponent && <ActiveComponent />}
    </div>
  );
};

export default LogsDashboard;
